import { createContext, useContext, useEffect, useState, useCallback } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { setActAs } from "@/lib/actAs";

export const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null); // null = checking, false = anon, object = authed

  const checkSession = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/me", { noActAs: true });
      setUser(data);
    } catch {
      setUser(false);
    }
  }, []);

  useEffect(() => {
    checkSession();
  }, [checkSession]);
  useEffect(() => {
    if (!user) return undefined;
    import("@/lib/pendingQuote").then((m) => { m.setPendingOwner(user.id); m.retryPending(); });
    import("@/lib/offline").then((m) => { setTimeout(() => { m.warmOfflineCache(); }, 4000); });
    const t = setInterval(() => { api.post("/auth/refresh").catch(() => {}); }, 10 * 60 * 1000);
    return () => { clearInterval(t); };
  }, [user?.id]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    const onExpired = () => { setUser(false); };
    window.addEventListener("auth:expired", onExpired);
    return () => { window.removeEventListener("auth:expired", onExpired); };
  }, []);

  const login = async (email, password) => {
    setActAs(null);
    // Données hors ligne d'un éventuel autre compte sur cet appareil
    await import("@/lib/offline").then((m) => m.clearOfflineData()).catch(() => {});
    await api.post("/auth/login", { email, password });
    const { data } = await api.get("/auth/me", { noActAs: true });
    setUser(data);
    return data;
  };

  const register = async (payload) => {
    const { data } = await api.post("/auth/register", payload);
    setUser(data);
    return data;
  };

  const logout = async () => {
    setActAs(null);
    try {
      await api.post("/auth/logout");
    } catch {}
    import("@/lib/offline").then((m) => { m.clearOfflineData(); });
    setUser(false);
  };

  return (
    <AuthContext.Provider value={{ user, setUser, login, register, logout, checkSession }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  return useContext(AuthContext);
}

export { formatApiErrorDetail };
